/**
 * Apply shared device-sync records into the local trip cache and stamp overlays.
 */

import type { DeviceSyncRecord } from './deviceSyncService';
import {
  lastSyncStorageKey,
  parseCanonicalTripId,
  type DeviceSyncChangeRecord,
} from './sharedTripSyncService';
import {
  getAllTrips,
  getUserIdForTripWrites,
  persistSharedTripDeviceSyncRecords,
} from './tripsService';

export function toDeviceSyncRecords(records: DeviceSyncChangeRecord[]): DeviceSyncRecord[] {
  return records.map((r) => {
    const fromCanonical = parseCanonicalTripId(
      typeof r.body?.canonicalTripId === 'string' ? r.body.canonicalTripId : ''
    );
    const fromId = parseCanonicalTripId(r.id);
    const authorSub = fromCanonical?.authorSub ?? fromId?.authorSub;
    const body = { ...(r.body ?? {}) };
    // Ensure trip-v1 bodies carry tripId (some payloads only put it on the record id).
    if (r.type === 'trip-v1' && !String(body.tripId ?? '').trim()) {
      const tripId = fromCanonical?.tripId ?? fromId?.tripId;
      if (tripId) body.tripId = tripId;
    }
    return {
      id: r.id,
      type: r.type,
      body,
      updatedAt: r.updatedAt ?? undefined,
      userId: authorSub,
    };
  });
}

/**
 * Merge a shared-trip Device Sync pull into the invitee's local trip list.
 * Persists trip shell + cities (iOS SharedTripSyncService.apply parity) so My Trips
 * can show the trip once membership ids are cached.
 */
export function mergeSharedTripRecordsIntoLocalStore(
  canonicalTripId: string,
  records: DeviceSyncChangeRecord[]
): void {
  const userId = getUserIdForTripWrites();
  if (!userId) return;
  const parsed = parseCanonicalTripId(canonicalTripId);
  if (!parsed) return;

  persistSharedTripDeviceSyncRecords(userId, canonicalTripId, toDeviceSyncRecords(records));

  // Advance incremental cursor only after the trip is actually local.
  const hasTrip = getAllTrips(userId).some(
    (t) => String(t.id).toLowerCase() === parsed.tripId.toLowerCase()
  );
  if (!hasTrip) return;
  const maxUpdated = records
    .map((r) => r.updatedAt)
    .filter((v): v is string => Boolean(v))
    .sort()
    .at(-1);
  if (maxUpdated) {
    localStorage.setItem(lastSyncStorageKey(canonicalTripId), maxUpdated);
  }
}
